import axios from "axios";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";

const PaymentFailed = () => {
  const { clientId } = useParams();

  const [isRetrying, setIsRetrying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const whatsappNumber = "2348069804062";

  const whatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Zaviramed, my subscription payment failed and I need help retrying. My client ID is: ${
        clientId || "not available"
      }`
    );
  }, [clientId]);

  const emailHref = useMemo(() => {
    const subject = encodeURIComponent("Subscription Payment Failed");
    const body = encodeURIComponent(
      `Hello Zaviramed,\n\nMy subscription payment failed and I need help retrying.\n\nClient ID: ${
        clientId || "not available"
      }\n\nPlease assist me.`
    );

    return `mailto:support@zaviramed.com.ng?subject=${subject}&body=${body}`;
  }, [clientId]);

  const handleRetry = async () => {
    try {
      setIsRetrying(true);
      setErrorMessage("");

      if (!clientId) {
        setErrorMessage(
          "We could not find your client ID. Please contact Zaviramed support."
        );
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (!backendUrl) {
        setErrorMessage(
          "Payment retry is not configured correctly. Please contact Zaviramed support."
        );
        return;
      }

      const res = await axios.post(
        `${backendUrl}/api/paystack/retry`,
        { clientId },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

      const newUrl = res.data?.newUrl;

      if (!newUrl) {
        setErrorMessage(
          res.data?.message ||
            "We could not generate a new payment link. Please contact support."
        );
        return;
      }

      window.location.href = newUrl;
    } catch (err) {
      console.error("Payment retry error:", err);

      if (err.code === "ECONNABORTED") {
        setErrorMessage(
          "The request took too long. Please check your internet connection and try again."
        );
        return;
      }

      setErrorMessage(
        err.response?.data?.message ||
          "We could not restart your payment. Please contact Zaviramed support."
      );
    } finally {
      setIsRetrying(false);
    }
  };

  const openWhatsappSupport = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${whatsappText}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openEmailSupport = () => {
    window.location.href = emailHref;
  };

  return (
    <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
      <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
          {/* Left Panel */}
          <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div>
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-12 w-auto object-contain"
              />

              <div className="mt-16">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-red-300">
                  <XCircle size={15} />
                  Payment not completed
                </div>

                <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                  Your subscription payment could not be completed.
                </h1>

                <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                  You can safely retry the payment to activate your unlimited
                  Zaviramed consultations.
                </p>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-slate-300">
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Service</span>
                <span className="font-medium text-white">Subscription</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Provider</span>
                <span className="font-medium text-white">Zaviramed</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Status</span>
                <span className="font-medium text-red-300">Failed</span>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <section className="p-5 sm:p-8 md:p-10 lg:p-12">
            <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-10 w-auto object-contain"
              />

              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <ShieldCheck size={14} />
                Secure
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
              <AlertCircle size={14} />
              Payment failed
            </div>

            <div className="mt-8 max-w-2xl">
              <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border border-red-200 bg-red-50 text-red-700">
                <XCircle size={32} />
              </div>

              <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                We could not complete your subscription payment
              </h2>

              <p className="mt-4 text-base leading-7 text-slate-600">
                Your unlimited consultation access has not been activated yet.
                You can retry payment now or contact Zaviramed support if you
                were debited.
              </p>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm leading-6 text-slate-700">
                  Please try again to activate your Zaviramed subscription. If
                  money was deducted from your account, do not pay repeatedly —
                  contact support with your client ID.
                </p>
              </div>

              {errorMessage && (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4">
                  <p className="text-sm leading-6 text-red-700">
                    {errorMessage}
                  </p>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="mt-8 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Payment details
                </p>
              </div>

              <div className="grid gap-0 sm:grid-cols-2">
                <div className="border-b border-slate-200 px-4 py-4 sm:border-b-0 sm:border-r">
                  <p className="text-xs font-medium text-slate-500">
                    Payment purpose
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">
                    Unlimited consultation subscription
                  </p>
                </div>

                <div className="px-4 py-4">
                  <p className="text-xs font-medium text-slate-500">
                    Client ID
                  </p>
                  <p className="mt-1 truncate text-sm font-semibold text-slate-950">
                    {clientId || "Not available"}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleRetry}
                disabled={isRetrying}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-4 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
              >
                {isRetrying ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <RefreshCw size={18} />
                )}
                {isRetrying ? "Generating new link" : "Try payment again"}
              </button>

              <button
                type="button"
                onClick={openEmailSupport}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-50 active:scale-[0.99] sm:w-auto"
              >
              <Mail size={18} />
                 support
              </button>

           
            </div>

            <div className="mt-10 grid gap-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  If you were not debited, you can safely retry the payment.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  If you were debited, contact support before making another
                  payment.
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default PaymentFailed;